import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { heroSlides } from '../data/siteContent';

const pieces = [
  { id: 1, width: '25%', height: '33.33%', x: '0%', y: '0%' },
  { id: 2, width: '25%', height: '33.33%', x: '25%', y: '0%' },
  { id: 3, width: '25%', height: '33.33%', x: '50%', y: '0%' },
  { id: 4, width: '25%', height: '33.33%', x: '75%', y: '0%' },
  { id: 5, width: '25%', height: '33.33%', x: '0%', y: '33.33%' },
  { id: 6, width: '25%', height: '33.33%', x: '25%', y: '33.33%' },
  { id: 7, width: '25%', height: '33.33%', x: '50%', y: '33.33%' },
  { id: 8, width: '25%', height: '33.33%', x: '75%', y: '33.33%' },
  { id: 9, width: '25%', height: '33.33%', x: '0%', y: '66.66%' },
  { id: 10, width: '25%', height: '33.33%', x: '25%', y: '66.66%' },
  { id: 11, width: '25%', height: '33.33%', x: '50%', y: '66.66%' },
  { id: 12, width: '25%', height: '33.33%', x: '75%', y: '66.66%' },
];

const pieceExit = (piece) => ({
  hidden: {
    opacity: 0,
    y: -1000,
    rotate: Math.random() * 45 - 22.5,
    scale: 0.5,
  },
  visible: {
    opacity: 1,
    y: 0,
    rotate: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      delay: piece.id * 0.05,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
  exit: {
    opacity: 0,
    y: -800,
    rotate: Math.random() * 30 - 15,
    scale: 0.3,
    transition: {
      duration: 0.4,
      delay: piece.id * 0.02,
      ease: 'easeIn',
    },
  },
});

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const activeSlide = heroSlides[currentSlide];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);

    return () => clearInterval(timer);
  }, []);

  return (
    <section className="hero-slider">
      <AnimatePresence mode="sync">
        <motion.div
          key={currentSlide}
          className="slide"
          style={{ backgroundImage: `url(${activeSlide.image})` }}
        >
          <div className="slide-overlay" />
          <div className="hero-floating-card hero-floating-card-left">
            <span className="hero-floating-label">Hindi Support</span>
            <strong>हर स्टेप की सरल समझ</strong>
          </div>
          <div className="hero-floating-card hero-floating-card-right">
            <span className="hero-floating-label">Trusted Care</span>
            <strong>Diabetes + Kidney Guidance</strong>
          </div>

          <div className="slide-pieces">
            {pieces.map((piece) => (
              <motion.div
                key={piece.id}
                className="slide-piece"
                style={{
                  position: 'absolute',
                  left: piece.x,
                  top: piece.y,
                  width: piece.width,
                  height: piece.height,
                  backgroundImage: `url(${activeSlide.image})`,
                  backgroundSize: '400% 300%',
                  backgroundPosition: `${piece.x} ${piece.y}`,
                }}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={pieceExit(piece)}
              />
            ))}
          </div>

          <div className="slide-content-wrapper">
            <motion.div
              className="slide-content"
              initial={{ opacity: 0, scale: 0.86 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.75, duration: 0.5 }}
            >
              <motion.span
                className="slide-eyebrow"
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.85, duration: 0.45 }}
              >
                {activeSlide.eyebrow}
              </motion.span>
              <motion.h1
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.95, duration: 0.5 }}
              >
                {activeSlide.title}
              </motion.h1>
              <motion.p
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.08, duration: 0.5 }}
              >
                {activeSlide.subtitle}
              </motion.p>
              <motion.p
                className="slide-description"
                initial={{ y: 22, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.18, duration: 0.5 }}
              >
                {activeSlide.description}
              </motion.p>
              <motion.div
                className="slide-buttons"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.3, duration: 0.5 }}
              >
                <a href="/contact" className="slide-btn primary">Book Appointment</a>
                <a href="/kill-diabetes" className="slide-btn secondary">इलाज की जानकारी</a>
              </motion.div>
              <motion.div
                className="slide-stats"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.42, duration: 0.5 }}
              >
                {activeSlide.stats.map((stat) => (
                  <div key={stat.label} className="slide-stat">
                    <strong>{stat.value}</strong>
                    <span>{stat.label}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </AnimatePresence>

      <motion.div className="slider-progress" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <motion.div
          key={currentSlide}
          className="slider-progress-bar"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 6, ease: 'linear' }}
        />
      </motion.div>

      <div className="slider-dots">
        {heroSlides.map((slide, index) => (
          <motion.button
            key={slide.id}
            className={`dot ${index === currentSlide ? 'active' : ''}`}
            onClick={() => setCurrentSlide(index)}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      <div className="slider-arrows">
        <motion.button
          className="arrow prev"
          onClick={() => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          aria-label="Previous slide"
        >
          &#10094;
        </motion.button>
        <motion.button
          className="arrow next"
          onClick={() => setCurrentSlide((prev) => (prev + 1) % heroSlides.length)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          aria-label="Next slide"
        >
          &#10095;
        </motion.button>
      </div>
    </section>
  );
};

export default HeroSlider;
